import moment from 'moment';
import { getBackendUri } from '../utils/EnvConfigUtils';
import FetchUtils, { parseJSON } from './fetch_utils';
import { BACKEND_DATE_FORMAT } from '../utils/DataFormatterUtils';
import { crushFilters } from '../utils/DataAccessUtils';

const fetchUtils = new FetchUtils();
const baseUrl = getBackendUri();
const apiUrl = (userType: string) => `/api/v1/${userType}/kpi/kpi_reportings`;

/**
 * Convert moment / Date / ISO-string values into the backend's expected
 * `MM/DD/yyyy HH:mm:ss` shape. Strings that already look formatted are passed through.
 * Returns null when the input is not a parseable date so the caller can bail out.
 */
export const toBackendDateString = (value: any): string | null => {
	if (!value) return null;
	if (typeof value === 'string' && /^\d{2}\/\d{2}\/\d{4}/.test(value)) return value;
	const parsed = moment.isMoment(value) ? value : moment(value);
	if (!parsed || !parsed.isValid()) return null;
	return parsed.format(BACKEND_DATE_FORMAT);
};

/**
 * Per-KPI api substring registry — mirrors `fetchKPIByMonth` in kpi_analytics_thunks.ts. Keep in
 * sync when new KPI endpoints are added.
 */
export const KPI_COMPARISON_API_SUBSTRINGS = {
	workOrders: 'monthly_work_order_kpis',
	checkInCompliance: 'monthly_check_in_kpis',
	timeToAccept: 'monthly_time_to_accept_kpis',
	timeToSchedule: 'monthly_time_to_schedule_kpis',
	firstTimeResolution: 'monthly_resolved_first_time_kpis',
	onTimeArrival: 'monthly_on_time_arrival_kpis',
	timelyInvoicing: 'monthly_invoiced_within_30days_time_kpis',
	timelyWorkCompletion: 'monthly_work_completed_before_due_kpis',
} as const;

export type KPIComparisonKey = keyof typeof KPI_COMPARISON_API_SUBSTRINGS;

/**
 * Fetches a KPI monthly endpoint without dispatching to Redux — used to load comparison-period
 * data for a single chart card. Mirrors `fetchSpendBreakdownComparisonSilent` /
 * `fetchWorkBreakdownComparisonSilent` so the page can hold per-chart previous-period rows in
 * local state without affecting global loading flags.
 */
export const fetchKPIComparisonSilent =
	(apiSubstring: string) =>
	(userType: string) =>
	(params: Record<string, any>): Promise<any[]> => {
		// `useAnalyticsBreakdownComparison` hands us comparison `startDate` / `endDate` as raw
		// moment objects (it spreads them into the params blob without formatting). The KPI
		// endpoint expects `MM/DD/yyyy HH:mm:ss` strings — passing the moment serializes via
		// `Date.toString()` (e.g. "Thu Nov 28 2024 17:45:35 GMT+0530") and the backend 500s.
		const startDate = toBackendDateString(params.startDate);
		const endDate = toBackendDateString(params.endDate);
		if (!startDate || !endDate) {
			return Promise.resolve([]);
		}

		const queryParams = { ...params, startDate, endDate };
		const queryUri = baseUrl + apiUrl(userType) + '/' + apiSubstring;

		return fetchUtils
			.get(
				queryUri,
				Object.assign(
					{ headers: { Accept: 'application/json', 'Content-Type': 'application/json' } },
					crushFilters(queryParams)
				)
			)
			.then(parseJSON)
			.then(({ json, ok }) => {
				const responseBody = json;
				if (!ok || responseBody.status === 'error') {
					return Promise.reject(responseBody.message);
				}
				return responseBody.data || [];
			});
	};
